/**
 * The screen-level wrapper around useAwayGuard: fetches the tenant's policy, enforces it,
 * and tells the student what happened.
 *
 *   <AwayGuard area="assessment" onBeforeSignOut={handInIn} />   inside a quiz
 *   <AwayGuard area="reading" />                                 inside a lesson
 *
 * `onBeforeSignOut` is awaited before the session ends — that is where a quiz hands in
 * what the student had answered, so the attempt counts.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import useAwayGuard, { AWAY_REASON, ELSEWHERE_REASON } from '../../hooks/useAwayGuard';
import { loginPathForSession } from '../../lib/session';
import { Warning, Clock } from '@phosphor-icons/react';
import { Button } from '../ui/button';

/** Where the policy says the rule bites. */
const APPLIES = {
  assessments: ['assessment'],
  learning: ['assessment', 'reading'],
  everywhere: ['assessment', 'reading', 'other'],
};

/** Survives the sign-out so the login page can explain itself. */
export const SIGNED_OUT_KEY = 'lms_signed_out_reason';

export function takeSignOutReason() {
  try {
    const raw = sessionStorage.getItem(SIGNED_OUT_KEY);
    if (raw) sessionStorage.removeItem(SIGNED_OUT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export default function AwayGuard({ area = 'reading', onBeforeSignOut, disabled = false }) {
  const { api, user, logout } = useAuth();
  const [policy, setPolicy] = useState(null);
  const signingOut = useRef(false);

  // Only a learner is ever signed out this way; the server says so too, but asking for
  // the policy at all on a staff screen is noise.
  const isStudent = user?.role === 'learner';

  useEffect(() => {
    if (!isStudent) { setPolicy(null); return undefined; }
    let live = true;
    api.get('/tenant/session-policy')
      .then((r) => live && setPolicy(r.data || null))
      .catch(() => live && setPolicy(null));
    return () => { live = false; };
  }, [api, isStudent]);

  const inScope = !!policy && (APPLIES[policy.scope] || APPLIES.learning).includes(area);
  const active = isStudent && inScope && !disabled;

  const expire = useCallback(async ({ reason, seconds }) => {
    if (signingOut.current) return;
    signingOut.current = true;

    // Hand the quiz in FIRST. If the session goes first the request has no token and the
    // attempt is left dangling in progress, which is exactly what must not happen.
    let note = null;
    try { note = await onBeforeSignOut?.({ reason, seconds }); }
    catch { note = 'We could not save your attempt.'; }

    try {
      sessionStorage.setItem(SIGNED_OUT_KEY, JSON.stringify({
        reason, seconds, area, note: note || null, at: Date.now(),
      }));
    } catch { /* private mode */ }

    // Clearing the session is enough: ProtectedRoute sends them to their own portal.
    //
    // Navigating by hand as well mounted the sign-in page TWICE — once from that
    // redirect and again after the reload — and the first mount consumed the reason, so
    // the student arrived with no explanation. A hard redirect stays as a fallback for
    // any screen that is not behind ProtectedRoute.
    const target = loginPathForSession({ role: user?.role });
    logout();
    window.setTimeout(() => {
      if (!window.location.pathname.includes('/login')) window.location.href = target;
    }, 600);
  }, [onBeforeSignOut, logout, area, user]);

  const { warning, armed, awaySeconds } = useAwayGuard({ policy, active, onExpire: expire });

  if (!armed) return null;

  return (
    <>
      {/* A quiet note so the rule is never a surprise. */}
      {area === 'assessment' && policy?.away_enabled !== false && (
        <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 border-b border-amber-200 text-[12px] text-amber-900"
          data-testid="away-guard-notice">
          <Warning className="w-4 h-4 flex-shrink-0" weight="fill" />
          <span>
            Stay on this screen. Switching to another tab or app for more than {awaySeconds} seconds
            signs you out{policy?.count_attempt ? ' and submits this attempt' : ''}.
          </span>
        </div>
      )}

      {/* The idle countdown — visible, and cancelled by any movement. */}
      {warning !== null && (
        <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4"
          data-testid="idle-warning">
          <div className="bg-white rounded-xl p-5 max-w-sm w-full text-center space-y-3">
            <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-700 grid place-items-center mx-auto">
              <Clock className="w-6 h-6" weight="fill" />
            </div>
            <h2 className="font-heading text-lg font-semibold">Still there?</h2>
            <p className="text-[13px] text-muted-foreground">
              You'll be signed out in <strong data-testid="idle-countdown">{warning}</strong> second{warning === 1 ? '' : 's'}
              {area === 'assessment' && policy?.count_attempt ? ', and this attempt will be submitted' : ''}.
            </p>
            {/* Clicking is activity, which is all it takes to cancel. */}
            <Button className="w-full" data-testid="stay-signed-in">I'm still here</Button>
          </div>
        </div>
      )}
    </>
  );
}

/** The line the sign-in page shows after one of these sign-outs. */
export function signedOutMessage(info) {
  if (!info) return null;
  if (info.reason === ELSEWHERE_REASON) {
    return 'You were signed out because this account was signed in on another device. '
      + 'Your organisation limits how many devices can be used at once.';
  }
  const seconds = Math.max(1, Math.round(info.seconds || 0));
  const base = info.reason === AWAY_REASON
    ? `You were signed out because you left the screen for more than ${seconds} seconds.`
    : 'You were signed out after a period of inactivity.';
  if (info.area === 'assessment') {
    return `${base} ${info.note || 'Your attempt was submitted with the answers you had given.'}`;
  }
  return base;
}
